import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { usePrevious } from "../../../../utilities/hooks";
import { rangeToHash, updateHash } from "../../../../utilities/hash";
import { parseNumber, parseMagnitude } from "../../../../utilities/magnitudes";
import { track } from "../../../../actions/dtm";
import { FILTER, LAST_FILTERED_PARAM } from "../../../../constants/config/dtm";
import Text from "./Text";
import "./style.css";
import Reset from "../Reset";
import { resetColumn } from "../../../../actions/filters";
import { isUrlParamMatching } from "../../../../utilities/filters";
import * as HASH from '../../../../constants/config/hash';

/**
 * @name Range
 * @function
 * @description Displays Range filter
 * @returns {JSX}
 * @param {*} props
 */
const Range = (props) => {
  const [value, setValue] = useState({
      min: determineValue(props.min),
      max: determineValue(props.max),
    }),
    prevParam = usePrevious(props.parameter),
    prevMax = usePrevious(value.max),
    prevMin = usePrevious(value.min);
  const [resetClicked, setResetClicked] = useState(false); // Track reset button click
  const [resetText, setResetText] = useState(false);
  const [errorPresent, setErrorPresent] = useState(false)
  const {minimized, newSort, sortPriority} = useSelector((state) => ({
    minimized: state.tools.minimized,
    newSort: state.filters.newSort,
    sortPriority: state.filters.sortPriority,
  }));
  const dispatch = useDispatch();

  useEffect(() => {
    if (resetClicked) {
      // Reset button clicked, update values to props.min and props.max
      setValue({
        min: determineValue(props.min),
        max: determineValue(props.max),
      });

      // Reset the resetClicked state
      setResetClicked(false);
    } 

      // only update after values change
      if (prevMin || prevMax) {
        let hash = null;
        // if values changed, update the filter
        if (prevMin !== value.min || prevMax !== value.max) {
          // compare min and max as magnitudes if necessary
          let max = props.magnitude
              ? parseNumber(props.max)
              : parseMagnitude(props.max),
            min = props.magnitude
              ? parseNumber(props.min)
              : parseMagnitude(props.min);

          if (value.min !== min || value.max !== max)
            hash = rangeToHash(props.parameter, value.max, value.min);
        }

        updateHash(props.parameter, hash);
        if (hash) {
          window[LAST_FILTERED_PARAM] = hash;
          dispatch(track(FILTER));
        }
      }

      // when the parameter changes, update
      if (prevParam !== props.parameter) {
        setValue({
          min: determineValue(props.min),
          max: determineValue(props.max),
        });
      }
    
  }, [props.min, props.max, value.min, value.max, resetClicked]);

  /**
   * @name determineValue
   * @function
   * @description Determines the appropriate value to display
   * @param {string|number} value
   * @param {boolean} isMin determines if the value being interpretted is the min
   */
  function determineValue(value, isMin = false) {
    switch (typeof value) {
      case "number":
        if (isNaN(value)) value = isMin ? props.min : props.max;
        break;
      default:
        if (value.length > 0 && isNaN(value.charAt(0)))
          value = isMin ? props.min : props.max;
        break;
    }
    return props.magnitude ? parseNumber(value) : parseMagnitude(value);
  }

  /**
   * @name checkUnfinishedNumber
   * @funciton
   * @description Determines if the user is not done writing their number
   * @param {string} str String form of the number
   * @returns {boolean} Returns `true` if the number is unfinished, `false` if it is finished
   */
  const checkUnfinishedNumber = (str) => {
    // if the value is a number, its done
    if (typeof str === "number") return false;

    // if there's no length, its not done
    if (str.length === 0) return true;

    // if the last character in the string is a decimal, the user isn't done typing
    return str.charAt(str.length - 1) === ".";
  };

  /**
   * @name onConversion
   * @function
   * @description Updates the min/max filter values after they've been converted on input blur
   * @param {number|string} min
   * @param {number|string} max
   */
  const onConversion = (min, max) => setValue({ min, max });

  /**
   * @name onChangeMax
   * @function
   * @description Sets the max filter value
   * @param {EventListenerObject|number} e Synthetic browser event
   */
  const onChangeMax = (e) => {
    let newValue = { ...value },
      number = typeof e === "number" ? e : e.target.value;
    if (checkUnfinishedNumber(String(number))) return;
    newValue.max = determineValue(number);
    setValue(newValue);

    if (newValue.min !== '' && newValue.max !== '') {
      const adobeDataName = props.name;
      const rangeValue = `${newValue.min} to ${newValue.max}`;
      const dataForAdobeDataLayer = {
          event: "PST Table Interaction",
          pstInteractionsInfo: {
            [adobeDataName]: rangeValue
          }
    };

    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push(dataForAdobeDataLayer);
  }
  };

  /**
   * @name onChangeMin
   * @function
   * @description Sets the min filter value
   * @param {EventListenerObject} e Synthetic browser event
   */
  const onChangeMin = (e) => {
    let newValue = { ...value },
      number = typeof e === "number" ? e : e.target.value;
    if (checkUnfinishedNumber(String(number))) return;
    newValue.min = determineValue(number, true);
    setValue(newValue);

    if (newValue.min !== '' && newValue.max !== '') {
      const adobeDataName = props.name;
      const rangeValue = `${newValue.min} to ${newValue.max}`;
      const dataForAdobeDataLayer = {
        event: "PST Table Interaction",
        pstInteractionsInfo: {
          [adobeDataName]: rangeValue
        }
      };
  
      window.adobeDataLayer = window.adobeDataLayer || [];
      window.adobeDataLayer.push(dataForAdobeDataLayer);
    } 
  };

  /**
   * @name resetFilter
   * @function
   * @description resets the states
   */
  const resetFilter = () => {
    setResetClicked(true); // Set the resetClicked state to trigger the reset
    setResetText(true);
    if (newSort && newSort === props.parameter) {
      dispatch(resetColumn(true, props.parameter));
    }
    setTimeout(() => setResetText(false), 0);
  };
  const isResetVisible =
    window.location.hash !== HASH.INITIATOR &&
    (
      value.min != determineValue(props.min) ||
      value.max != determineValue(props.max) ||
      isUrlParamMatching(props.parameter)
    );

  return (
    <div className={`range-filter ${errorPresent? 'range-gap':''}`}>
      <div className="range-filter-container">
        {minimized && !props.mobile ? (
          <Text
            magnitude={props.magnitude}
            max={value.max}
            min={value.min}
            onConversion={onConversion}
            onMaxChange={onChangeMax}
            onMinChange={onChangeMin}
            parameter={props.parameter}
            rangeMax={determineValue(props.max)}
            rangeMin={determineValue(props.min)}
            resetText={resetText}
            setErrorPresent={setErrorPresent}
            {...props}
          />
        ) : (
          ""
        )}
      </div>
      {(isResetVisible ||
        (newSort === props.parameter && sortPriority.length > 0)) && (
        <Reset onClick={resetFilter} />
      )}
    </div>
  );
};

Range.propTypes = {
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  parameter: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  mobile: PropTypes.bool,
};

export default Range;
